import { useGenerationRealtime } from '@/hooks/use-generation-realtime';
import { env } from '@/lib/env';
import { handleError } from '@/lib/error/handle';
import { mutate } from 'swr';
import { toast } from 'sonner';
import { reuseOrAppendHistory } from '@/components/nodes/shared/media-history';

type TranslateFn = (key: string, vars?: Record<string, unknown>) => string;

// Hook 入参类型
export type UseImageRealtimeParams = {
    // 项目 ID
    projectId?: string | null;
    // 节点 ID
    nodeId: string;
    // 节点数据
    data: any;
    // 模型 ID
    modelId: string;
    // 更新节点数据回调
    updateNodeData: (id: string, partial: any) => void;
    // 获取最新节点数据（避免闭包陈旧数据）
    getNode: (id: string) => any;
    // 翻译函数
    t: TranslateFn;
};

export function useImageRealtime({ projectId, nodeId, data, modelId, updateNodeData, getNode, t }: UseImageRealtimeParams) {
    const isRealtimeEnabled =
        String(env.NEXT_PUBLIC_ENABLE_REALTIME ?? 'true') !== 'false' &&
        Boolean(projectId) &&
        (data as any)?.status === 'processing';

    useGenerationRealtime({
        projectId: projectId ?? '',
        nodeId,
        generationId: (data as any)?.processingGenerationId as string | undefined,
        enabled: isRealtimeEnabled,
        onCompleted: (url, metadata) => {
            const nowIso = new Date().toISOString();
            // 从 React Flow store 读取最新节点数据，避免闭包陈旧数据导致字段回退
            const latestNode = (() => { try { return getNode(nodeId); } catch { return undefined; } })();
            const latestData = (latestNode as any)?.data ?? {};

            const responseMeta = (metadata as any)?.response ?? {};
            const urls: string[] = Array.isArray((responseMeta as any)?.urls)
                ? (responseMeta as any).urls.filter(Boolean)
                : (typeof url === 'string' && url ? [url] : []);

            const inferredType = ((metadata as any)?.type as string | undefined) || 'image/png';

            // 服务端在 metadata.response 里带回了真实尺寸（来自解析图片字节），直接采用以避免客户端再去解析文件头。
            const respSize = typeof (responseMeta as any)?.size === 'string' ? (responseMeta as any).size : undefined;
            const hasRealSize = Boolean(respSize && /^\d+x\d+$/i.test(respSize));
            const realMeta = hasRealSize ? { size: respSize, sizeUrl: urls[0] ?? url } : {};

            const { history: nextHistory, activeHistoryId: nextActiveId } = reuseOrAppendHistory(
                latestData?.history,
                urls,
                (id) => ({
                    id,
                    ...(urls.length > 1 ? { urls } : { url: urls[0] }),
                    type: inferredType,
                    description: '',
                    createdAt: nowIso,
                    prompt: latestData?.instructions ?? '',
                    modelId,
                    // 历史条目存“真实输出像素”（绑定该条目 url）；服务端未解析出时回退到设置值。
                    // resolveImageHistorySize 能同时识别真实像素与设置值，切历史时据此恢复下拉与展示尺寸。
                    size: hasRealSize ? respSize : latestData?.size,
                }),
            );

            const prevGenerated = latestData?.generated ?? {};
            const nextGenerated = urls.length > 0
                ? { ...prevGenerated, url: urls[0], urls, type: inferredType, ...realMeta }
                : { ...prevGenerated, url, type: inferredType, ...realMeta };

            // 仅写入需要变更的字段，updateNodeData 会浅合并到现有数据
            updateNodeData(nodeId, {
                updatedAt: nowIso,
                status: 'completed',
                model: modelId,
                generated: nextGenerated,
                history: nextHistory,
                activeHistoryId: nextActiveId,
                processingGenerationId: undefined,
                runtimeEstimatedTime: undefined,
            });
            toast.success(t('Image generated successfully'));
            setTimeout(() => mutate(projectId ? `credits:${projectId}` : 'credits'), 5000);
        },

        onFailed: (err) => {
            const nowIso = new Date().toISOString();
            handleError(t('Error generating image'), err);
            updateNodeData(nodeId, {
                status: 'failed',
                updatedAt: nowIso,
                processingGenerationId: undefined,
                runtimeEstimatedTime: undefined,
                error: (typeof err === 'string') ? err : ((err as any)?.message ?? String(err ?? '')),
            });
        },
    });
}
